import {injectable} from 'inversify';
import {makeAutoObservable, observable} from 'mobx';

import type {ApiListResponse, Entity} from '../../../api/types';
import type {
  ApiRpcCursor,
  ApiRpcRequestParams,
} from '../../../api/utils/apiRequest';
import type {Nullable} from '../../../utils/utility-types';
import type {ApiCollectionState} from './ApiCollectionState';

import {isDefined} from '../../../utils/typecheck';

const DEFAULT_LIMIT = 100;

@injectable()
export class ApiCollectionStateJrpc<
  P extends ApiCollectionStateJrpc.Params = ApiCollectionStateJrpc.Params,
  R extends ApiCollectionStateJrpc.Response = ApiCollectionStateJrpc.Response,
  C extends ApiCollectionStateJrpc.Cursor = ApiCollectionStateJrpc.Cursor,
> implements ApiCollectionState<P, R, C>
{
  state: ApiCollectionState.State<C> = this.makeEmptyState();
  private isCursorMode = false;
  private nextParams: P = {} as P;
  private shouldOverwriteCursorState = false;
  private shouldOverwriteLimit = false;

  constructor() {
    makeAutoObservable(
      this,
      {
        state: observable.ref,
      },
      {
        autoBind: true,
      },
    );
  }

  configure(config?: ApiCollectionState.InitConfig): this {
    this.isCursorMode = config?.isCursorBased ?? false;

    return this;
  }

  getNextApiParams(params?: P): P {
    const mergedParams = {
      ...params,
    } as P;

    // we don't allow passing offset
    mergedParams.offset = undefined;

    // In offset mode we need to increase the limit by 1 to check if there are more items
    const shouldIncreaseLimit = this.isOffsetMode;

    mergedParams.limit =
      (this.shouldOverwriteLimit
        ? this.nextParams.limit
        : mergedParams.limit) || DEFAULT_LIMIT;

    if (shouldIncreaseLimit) {
      mergedParams.limit += 1;
    }

    mergedParams.cursor = this.nextParams.cursor || mergedParams.cursor;

    // merge other params
    for (const key of Object.keys(this.nextParams)) {
      const shouldSkip = key === 'limit' || key === 'cursor';

      if (shouldSkip) {
        continue;
      }

      // @ts-expect-error: TODO: describe why
      mergedParams[key] = this.nextParams[key];

      if (
        key === 'query' &&
        Array.isArray(params?.query) &&
        Array.isArray(mergedParams.query)
      ) {
        const mergedQuery = params.query;

        for (const value of mergedParams.query) {
          if (!mergedQuery.find(q => q?.[0] === value?.[0])) {
            mergedQuery.push(value);
          }
        }

        mergedParams.query = mergedQuery;
      }
    }

    this.nextParams = mergedParams;

    return mergedParams;
  }

  next(config?: ApiCollectionState.BuildStateConfig<C>): this {
    return this.buildNextParams(config, 'next');
  }

  parseApiResponse(response: R): this {
    const nextCursorState = this.parseCursor(response);
    const nextHasState = this.parseHasState(response);

    /**
     * In offset mode we increase limit by 1 to check if there are more items
     * so we need to remove the last item from the list
     */
    if (this.isOffsetMode && nextHasState.next) {
      response._items.pop();
    }

    this.state = {
      cursor: nextCursorState,
      entitiesCount: this.state.entitiesCount + response._items.length,
      has: nextHasState,
    };

    this.nextParams = {} as P;

    return this;
  }

  prev(config?: ApiCollectionState.BuildStateConfig<C>): this {
    return this.buildNextParams(config, 'prev');
  }

  reset() {
    this.state = this.makeEmptyState();

    this.nextParams = {} as P;

    return this;
  }

  private get isOffsetMode() {
    return !this.isCursorMode;
  }

  private buildNextParams(
    config: ApiCollectionState.BuildStateConfig<C> | undefined,
    direction: 'next' | 'prev',
  ): this {
    const {cursor, limit} = config || {};

    const nextCursor = this.getNextCursor(cursor, direction);

    const nextLimit = limit || DEFAULT_LIMIT;

    this.shouldOverwriteLimit = isDefined(limit);

    const nextOffset = this.getNextOffset(direction, nextLimit);

    this.nextParams = {
      ...this.nextParams,
      cursor: nextCursor,
      limit: nextLimit,
      offset: nextOffset,
    };

    return this;
  }

  private getNextCursor(
    cursor: C | undefined,
    direction: ApiCollectionStateJrpc.Direction,
  ): C | undefined {
    if (cursor && this.isOffsetMode) {
      // eslint-disable-next-line no-console
      console.warn(
        'ApiRequestStateJrpc: Attempt to use cursor in offset mode. Cursor will be ignored. Use init({isCursorBased: true}) to enable cursor mode.',
      );

      return;
    }

    let nextCursor: C | undefined;

    switch (true) {
      case isDefined(cursor):
        nextCursor = cursor;
        this.shouldOverwriteCursorState = true;
        break;
      case direction === 'next':
        nextCursor = this.state.cursor.next ?? undefined;
        break;
      case direction === 'prev':
      default:
        nextCursor = this.state.cursor.prev ?? undefined;
    }

    return nextCursor;
  }

  private getNextOffset(
    direction: ApiCollectionStateJrpc.Direction,
    nextLimit: number,
  ): number | undefined {
    if (this.isCursorMode) {
      return;
    }

    let nextOffset: number | undefined;

    switch (true) {
      case direction === 'next':
        nextOffset = this.state.entitiesCount;
        break;
      case direction === 'prev':
      default:
        nextOffset = Math.max(this.state.entitiesCount - nextLimit, 0);
    }

    if (nextOffset === 0) {
      nextOffset = undefined;
    }

    return nextOffset;
  }

  private makeEmptyState(): ApiCollectionState.State<C> {
    return {
      cursor: {
        next: null,
        prev: null,
      },

      entitiesCount: 0,

      has: {
        next: false,
        prev: false,
      },
    };
  }

  private parseCursor(response: R) {
    const nextCursor = (response._next ?? null) as Nullable<C>;
    const prevCursor = (response._prev ?? null) as Nullable<C>;

    if ((prevCursor || nextCursor) && this.isOffsetMode) {
      // eslint-disable-next-line no-console
      console.warn(
        'ApiRequestStateJrpc: the response contains cursor data, but the request is in offset mode. Use init({isCursorBased: true}) to enable cursor mode.',
      );
    }

    let result: ApiCollectionState.State<C>['cursor'] = {
      next: null,
      prev: null,
    };

    if (!nextCursor && !prevCursor) {
      return result;
    }

    const isNextCursorDirection = this.state.cursor?.[1] === 1;
    const isPrevCursorDirection = this.state.cursor?.[1] === -1;

    switch (true) {
      case this.shouldOverwriteCursorState:
        result.next = nextCursor;
        result.prev = prevCursor;
        break;
      case isNextCursorDirection:
        result.next = nextCursor;
        break;
      case isPrevCursorDirection:
        result.prev = prevCursor;
        break;
      default:
        result = {
          next: nextCursor,
          prev: prevCursor,
        };
        break;
    }

    return result;
  }

  private parseHasState(response: R) {
    const hasState: ApiCollectionState.State<C>['has'] = {
      ...this.state.has,
    };

    if (this.isOffsetMode) {
      hasState.next = response._items.length === this.nextParams.limit;
      hasState.prev = false;
    } else {
      hasState.next = response._next !== null;
      hasState.prev = response._prev !== null;
    }

    return hasState;
  }
}

export namespace ApiCollectionStateJrpc {
  export type Cursor = ApiRpcCursor;

  export type Direction = 'next' | 'prev';

  export type Params = ApiRpcRequestParams;

  export type Response = ApiListResponse<Entity>;
}
